import { updateConsents } from "@flow/common/consent_logic";
import { Consent, CrmSurveyResponse } from "@flow/common/types";
import { DragReorderer } from "@flow/frontend/common/DragReorderer";
import { ConsentInputComponentBlock } from "@flow/frontend/common/ConsentInputComponentBlock";
import { MainContainer } from "@flow/frontend/member/components/MainContainer";
import { memberTheme } from "@flow/frontend/member/memberTheme";
import { partitionConsentConfigs } from "@flow/frontend/member/member_utils";
import { ActionComponentProps, pluginRegistry } from "@flow/frontend/member/plugin_api";
import { bug } from "@flow/lib/error";
import { Text } from "@flow/lib/i18n";
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as React from "react";
import { ActionCustomData, RandomCommonQuestionAction_def } from "../../common/action_types";
import { storeSingleAnswer } from "../../common/routes";

pluginRegistry.registerActionComponent(RandomCommonQuestionAction_def, RandomCommonQuestionActionComponent);

/**
 * This is provided a question , or undefined.  If undefined (there are no questions to show this user) it will skip this action.
 * If it is provided a question, ask the user the question!
 */
export function RandomCommonQuestionActionComponent(props: ActionComponentProps<ActionCustomData>) {
  const [multiStringAnswer, setMultiStringAnswer] = React.useState<string[]>([]);
  const [stringAnswer, setStringAnswer] = React.useState("");
  const [boolAnswer, setBoolAnswer] = React.useState(false);
  const [consents, setConsents] = React.useState<Consent[]>([]);
  const [implicitCommonConsentConfigs, explicitCommonConsentConfigs] = partitionConsentConfigs(
    props.custom.consent_configs
  );
  const [alreadyConsentedTo] = React.useState<Consent[]>(props.consents || []);

  const formRef = React.useRef<HTMLFormElement>(null);

  const { dark } = memberTheme.palette.primary;
  const { grey300 } = memberTheme.palette.greys;

  const isSmallScreen = useMediaQuery(memberTheme.breakpoints.down("sm"));

  React.useEffect(() => {
    // no question for this member. skip to next step
    if (props.custom.question === undefined) {
      props.dispatch.gotoNextStep("skipped", props.ctx, alreadyConsentedTo);
    } else {
      if (props.custom.question.qtype === "ranking") {
        // set defaults for ranking question
        setMultiStringAnswer(props.custom.question.options || bug());
      }
    }
  }, []);

  if (props.custom.question === undefined) {
    return null;
  }

  return (
    <MainContainer sx={{ marginY: "1.5rem" }}>
      <Typography
        fontWeight="bold"
        sx={{
          typography: { xs: "subtitle1", md: "h6" },
          marginBottom: "1rem",
        }}
      >
        {props.custom.title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ marginBottom: "1rem" }}
        dangerouslySetInnerHTML={{ __html: props.custom.pre_question_html }}
      />
      <form ref={formRef}>
        {props.custom.question ? (
          <>
            <Typography fontWeight="bold" marginBottom="1rem">
              {props.custom.question.question}
            </Typography>

            {props.custom.question.qtype === "text" && (
              <>
                <TextField
                  name="answer"
                  required
                  placeholder="Enter your answer"
                  value={stringAnswer}
                  variant="outlined"
                  onChange={(e) => setStringAnswer(e.target.value)}
                />
              </>
            )}

            {props.custom.question.qtype === "text_area" && (
              <TextField
                fullWidth
                multiline
                required
                rows={3}
                variant="outlined"
                placeholder="Enter your answer"
                value={stringAnswer}
                onChange={(e) => setStringAnswer(e.target.value)}
              />
            )}

            {props.custom.question.qtype === "select" && (
              <FormControl fullWidth>
                <Select required label="" onChange={(e) => setStringAnswer(e.target.value)} value={stringAnswer}>
                  {props.custom.question.options?.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {props.custom.question.qtype === "radio_buttons" && (
              <RadioGroup value={stringAnswer} onChange={(e) => setStringAnswer(e.target.value)}>
                {props.custom.question.options?.map((opt) => (
                  <FormControlLabel key={opt} control={<Radio required />} value={opt} label={opt} />
                ))}
              </RadioGroup>
            )}

            {props.custom.question.qtype === "check_box" && (
              <FormControlLabel
                control={<Checkbox checked={boolAnswer} onChange={(e) => setBoolAnswer(e.target.checked)} />}
                label="Tick if 'yes'"
              />
            )}

            {props.custom.question.qtype === "check_boxes" &&
              props.custom.question.options?.map((opt) => (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      checked={multiStringAnswer.includes(opt)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setMultiStringAnswer([...multiStringAnswer, opt]);
                        } else {
                          setMultiStringAnswer(multiStringAnswer.filter((o) => o !== opt));
                        }
                      }}
                    />
                  }
                  label={opt}
                />
              ))}

            {props.custom.question.qtype === "ranking" && (
              <DragReorderer options={props.custom.question.options || bug()} onSetOrder={setMultiStringAnswer} />
            )}
          </>
        ) : null}
        {props.custom.post_question_html && (
          <>
            <Divider />
            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: props.custom.post_question_html }} />
          </>
        )}
        {explicitCommonConsentConfigs.length > 0 && (
          <ConsentInputComponentBlock
            consentConfigs={explicitCommonConsentConfigs}
            alreadyConsentedTo={alreadyConsentedTo}
            updateConsents={(changedConsents) => setConsents((c) => updateConsents(c, changedConsents))}
            displayTopBorder={true}
          />
        )}
        <Divider />
        <Button fullWidth={isSmallScreen} onClick={submitAnswer} sx={{ marginBottom: "1rem" }}>
          {props.custom.submit_button_label || <Text id="plugin.survey.submit">Submit</Text>}
        </Button>
        {"  "}
        <Button
          variant="contained"
          color="secondary"
          fullWidth={isSmallScreen}
          disableElevation
          onClick={() => props.dispatch.gotoNextStep("skipped", props.ctx, alreadyConsentedTo)}
          sx={{ backgroundColor: grey300, color: dark, marginBottom: "1rem" }}
        >
          {props.custom.skip_button_label || <Text id="plugin.survey.skip">Skip</Text>}
        </Button>
        {implicitCommonConsentConfigs.length > 0 && (
          <>
            <ConsentInputComponentBlock
              consentConfigs={implicitCommonConsentConfigs}
              alreadyConsentedTo={alreadyConsentedTo}
              updateConsents={(changedConsents) => setConsents((c) => updateConsents(c, changedConsents))}
              displayTopBorder={true}
            />
          </>
        )}
      </form>
    </MainContainer>
  );

  function submitAnswer(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    if (!formRef.current?.checkValidity()) {
      return;
    }

    if (!props.custom.question) {
      bug();
      return;
    }

    const allConsents = updateConsents(alreadyConsentedTo, consents);

    // consents is passed to the plugin backend - it is the list of consents a member saw on this step.
    // it is recorded in the DB and passed to the CRM.
    // allConsents is passed to goToNextStep - it is the list of all consents a member has given, including ones for
    // other plugins.  It is used for consents cookie.

    storeSingleAnswer
      .call({
        context: props.ctx,
        actionGuid: props.action.guid,
        consents: consents,
        answer: ((question): CrmSurveyResponse => {
          switch (question.qtype) {
            case "check_box":
              return {
                type: "boolean",
                external_question_id: question.id.toString(),
                question: question.question,
                answer: boolAnswer,
              };
            case "check_boxes":
              return {
                type: "multiselect",
                external_question_id: question.id.toString(),
                question: question.question,
                options: question.options || bug(),
                answer: multiStringAnswer,
              };
            case "ranking":
              return {
                type: "ranking",
                external_question_id: question.id.toString(),
                question: question.question,
                options: question.options || bug(),
                answer: multiStringAnswer,
              };
            case "radio_buttons":
            case "select":
            case "text":
            case "text_area":
              return {
                type: "text",
                external_question_id: question.id.toString(),
                question: question.question,
                answer: stringAnswer,
              };
          }
        })(props.custom.question),
      })
      .then(() => props.dispatch.gotoNextStep("completed", props.ctx, allConsents));
  }
}
